import {
    CheckCircleOutlined,
    CheckOutlined,
    CloseCircleOutlined,
    CompassOutlined,
    CopyOutlined,
    EnvironmentOutlined,
    ExclamationCircleOutlined,
    ThunderboltOutlined,
} from "@ant-design/icons";
import { Collapse, Switch, Tooltip, Typography } from "antd";
import PropTypes from "prop-types";
import { useState } from "react";

const { Text, Paragraph } = Typography;

const cn = (...classes) => classes.filter(Boolean).join(" ");

const qualityConfig = {
    ROOFTOP: {
        level: 4,
        label: "Exact Match",
        partialLabel: "Exact Match (Auto-corrected)",
        description: "Precise rooftop-level accuracy. Ready for delivery.",
        partialDescription: "Location found with minor corrections. Review the suggested address below.",
        icon: <CheckCircleOutlined />,
        color: "success",
    },
    RANGE_INTERPOLATED: {
        level: 3,
        label: "Street Range",
        description: "Matched to street range. Add suite or building number for better accuracy.",
        icon: <ExclamationCircleOutlined />,
        color: "warning",
    },
    GEOMETRIC_CENTER: {
        level: 2,
        label: "Area Level",
        description: "Resolves to street or zip code only. Provide full building number for routing.",
        icon: <CloseCircleOutlined />,
        color: "error",
    },
    APPROXIMATE: {
        level: 1,
        label: "Broad Region",
        description: "Points to city or state level. A complete street address is required.",
        icon: <CloseCircleOutlined />,
        color: "error",
    },
    EXACT_COORDINATES: {
        level: 4,
        label: "Manual Pin",
        description: "Location set manually via map pin.",
        icon: <EnvironmentOutlined />,
        color: "info",
    },
};

const colorStyles = {
    success: {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-700",
        iconBg: "bg-emerald-100",
        bar: "bg-emerald-500",
        badgeBg: "bg-emerald-100",
        badgeText: "text-emerald-700",
    },
    warning: {
        bg: "bg-amber-50",
        border: "border-amber-200",
        text: "text-amber-700",
        iconBg: "bg-amber-100",
        bar: "bg-amber-500",
        badgeBg: "bg-amber-100",
        badgeText: "text-amber-700",
    },
    error: {
        bg: "bg-red-50",
        border: "border-red-200",
        text: "text-red-700",
        iconBg: "bg-red-100",
        bar: "bg-red-500",
        badgeBg: "bg-red-100",
        badgeText: "text-red-700",
    },
    info: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700",
        iconBg: "bg-blue-100",
        bar: "bg-blue-500",
        badgeBg: "bg-blue-100",
        badgeText: "text-blue-700",
    },
};

function QualityMeter({ level, color }) {
    return (
        <Tooltip title={`Quality level ${level} of 4`}>
            <div className="flex items-center gap-1">
                {[1, 2, 3, 4].map((bar) => (
                    <div key={bar} className={cn("h-2 w-5 rounded-sm transition-colors", bar <= level ? colorStyles[color].bar : "bg-neutral-200")} />
                ))}
            </div>
        </Tooltip>
    );
}

QualityMeter.propTypes = {
    level: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
};

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    
    return (
        <Tooltip title={copied ? "Copied!" : "Copy"}>
            <button
                onClick={handleCopy}
                className="flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
            >
                {copied ? <CheckOutlined className="text-emerald-500" /> : <CopyOutlined />}
            </button>
        </Tooltip>
    );
}

CopyButton.propTypes = {
    text: PropTypes.string.isRequired,
};

export default function AddressHealthPanel({ geocoderMetadata, onUseGoogleAddressChange, className }) {
    if (!geocoderMetadata) return null;

    const { qualityScore, isPartialMatch, googleFormattedAddress, useGoogleAddress, coordinates } = geocoderMetadata;

    const config = qualityConfig[qualityScore] || qualityConfig["APPROXIMATE"]; // fallback
    const effectiveColor = isPartialMatch && qualityScore === "ROOFTOP" ? "warning" : config.color;
    const colors = colorStyles[effectiveColor];

    const displayLabel = isPartialMatch && config.partialLabel ? config.partialLabel : config.label;
    const displayDescription = isPartialMatch && config.partialDescription ? config.partialDescription : config.description;

    const effectiveLevel = isPartialMatch && qualityScore === "ROOFTOP" ? 3 : config.level;
    const isUnmailable = qualityScore === "APPROXIMATE" || qualityScore === "GEOMETRIC_CENTER";

    const hasCoordinates = coordinates?.lat != null && coordinates?.lng != null;
    const hasDetails = (googleFormattedAddress && qualityScore !== "EXACT_COORDINATES") || hasCoordinates;

    // Header content that's always visible
    const headerContent = (
        <div className="flex w-full items-start gap-4 pr-2 pt-1">
            {/* Icon */}
            <div className={cn("mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-lg", colors.iconBg, colors.text)}>{config.icon}</div>

            {/* Content */}
            <div className="min-w-0 flex-1">
                <div className="mb-1.5 flex items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <Text strong className={cn("text-base", colors.text)}>
                            {displayLabel}
                        </Text>
                        {isUnmailable && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Unmailable</span>}
                    </div>
                    <QualityMeter level={effectiveLevel} color={effectiveColor} />
                </div>

                <Paragraph className="!mb-0 text-sm leading-relaxed text-neutral-600" ellipsis={{ rows: 2 }}>
                    {displayDescription}
                </Paragraph>
            </div>
        </div>
    );

    // Expanded content
    const expandedContent = (
        <div className="space-y-4 pt-1">
            {/* Google formatted address */}
            {googleFormattedAddress && qualityScore !== "EXACT_COORDINATES" && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
                        <ThunderboltOutlined />
                        Standardized Address
                    </div>
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 bg-white p-3">
                        <Text className="text-sm font-medium text-neutral-800">{googleFormattedAddress}</Text>
                        <CopyButton text={googleFormattedAddress} />
                    </div>

                    {/* Use Google Address toggle */}
                    <div className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-white p-3">
                        <Switch checked={!!useGoogleAddress} onChange={onUseGoogleAddressChange} size="small" className="mt-0.5" />
                        <div className="space-y-1">
                            <Text className="block text-sm font-medium text-neutral-800">Use standardized address</Text>
                            <Text className="block text-xs text-neutral-500">
                                Apply Google&apos;s formatted version for downstream processing. Original data remains unchanged.
                            </Text>
                        </div>
                    </div>
                </div>
            )}

            {/* Coordinates */}
            {hasCoordinates && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
                        <CompassOutlined />
                        GPS Coordinates
                    </div>
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 bg-white p-3">
                        <code className="font-mono text-sm text-neutral-600">
                            {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
                        </code>
                        <CopyButton text={`${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`} />
                    </div>
                </div>
            )}
        </div>
    );

    if (!hasDetails) {
        // No expandable content - just render the header
        return <div className={cn("overflow-hidden rounded-xl border p-4", colors.border, colors.bg, className)}>{headerContent}</div>;
    }

    return (
        <Collapse
            bordered={false}
            expandIconPosition="end"
            className={cn("overflow-hidden rounded-xl", className)}
            items={[
                {
                    key: "1",
                    label: headerContent,
                    children: expandedContent,
                    className: cn(colors.bg, colors.border, "rounded-xl border"),
                },
            ]}
        />
    );
}

AddressHealthPanel.propTypes = {
    geocoderMetadata: PropTypes.shape({
        qualityScore: PropTypes.string,
        isPartialMatch: PropTypes.bool,
        googleFormattedAddress: PropTypes.string,
        useGoogleAddress: PropTypes.bool,
        coordinates: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number,
        }),
    }).isRequired,
    onUseGoogleAddressChange: PropTypes.func.isRequired,
    className: PropTypes.string,
};
